import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { ServicesViewState } from "../types";

interface ServicesUIState extends ServicesViewState {
  // Dialog states
  categoryDialogOpen: boolean;
  serviceDialogOpen: boolean;
  archiveDialogOpen: boolean;
  editingCategory: any | null;
  selectedCategoryForService: string | null;

  // Mobile UI states
  isSearchExpanded: boolean;
  isEditMode: boolean;

  // Service drag states
  serviceDragInProgress: boolean;
  activeServiceDragId: string | null;

  // Archive toggle state
  showArchived: boolean;

  // Actions
  setExpandedCategories: (categories: Set<string>) => void;
  toggleCategory: (categoryId: string) => void;
  setSearchTerm: (term: string) => void;
  setManuallyToggled: (toggled: boolean) => void;
  setIsDragInProgress: (inProgress: boolean) => void;
  setActiveDragId: (id: string | null) => void;

  // Mobile UI actions
  setIsSearchExpanded: (expanded: boolean) => void;
  setIsEditMode: (editMode: boolean) => void;

  // Service drag actions
  setServiceDragInProgress: (inProgress: boolean) => void;
  setActiveServiceDragId: (id: string | null) => void;

  // Archive toggle actions
  setShowArchived: (show: boolean) => void;

  // Dialog actions
  setCategoryDialogOpen: (open: boolean) => void;
  setServiceDialogOpen: (open: boolean) => void;
  setArchiveDialogOpen: (open: boolean) => void;
  setEditingCategory: (category: any | null) => void;
  setSelectedCategoryForService: (categoryId: string | null) => void;

  // Utility actions
  expandAllCategories: (categoryIds: string[]) => void;
  collapseAllCategories: () => void;
  autoExpandForSearch: (categoryIds: string[]) => void;
  reset: () => void;
}

const initialState: ServicesViewState = {
  expandedCategories: new Set(),
  searchTerm: "",
  manuallyToggled: false,
  isDragInProgress: false,
  activeDragId: null,
};

export const useServicesUIStore = create<ServicesUIState>()(
  devtools(
    (set, _get) => ({
      // Initial state
      ...initialState,
      categoryDialogOpen: false,
      serviceDialogOpen: false,
      archiveDialogOpen: false,
      editingCategory: null,
      selectedCategoryForService: null,

      // Mobile UI states
      isSearchExpanded: false,
      isEditMode: false,

      // Service drag states
      serviceDragInProgress: false,
      activeServiceDragId: null,

      // Archive toggle state
      showArchived: false,

      // State actions
      setExpandedCategories: (categories) => set({ expandedCategories: categories }),

      toggleCategory: (categoryId) =>
        set((state) => {
          const newExpanded = new Set(state.expandedCategories);
          if (newExpanded.has(categoryId)) {
            newExpanded.delete(categoryId);
          } else {
            newExpanded.add(categoryId);
          }
          return {
            expandedCategories: newExpanded,
            manuallyToggled: true,
          };
        }),

      setSearchTerm: (term) =>
        set({
          searchTerm: term,
          manuallyToggled: false, // Reset manual toggle when search changes
        }),

      setManuallyToggled: (toggled) => set({ manuallyToggled: toggled }),

      setIsDragInProgress: (inProgress) => set({ isDragInProgress: inProgress }),

      setActiveDragId: (id) => set({ activeDragId: id }),

      // Mobile UI actions
      setIsSearchExpanded: (expanded) => set({ isSearchExpanded: expanded }),

      setIsEditMode: (editMode) => set({ isEditMode: editMode }),

      // Service drag actions
      setServiceDragInProgress: (inProgress) => set({ serviceDragInProgress: inProgress }),

      setActiveServiceDragId: (id) => set({ activeServiceDragId: id }),

      // Archive toggle actions
      setShowArchived: (show) => set({ showArchived: show }),

      // Dialog actions
      setCategoryDialogOpen: (open) => set({ categoryDialogOpen: open }),

      setServiceDialogOpen: (open) => set({ serviceDialogOpen: open }),

      setArchiveDialogOpen: (open) => set({ archiveDialogOpen: open }),

      setEditingCategory: (category) => set({ editingCategory: category }),

      setSelectedCategoryForService: (categoryId) =>
        set({ selectedCategoryForService: categoryId }),

      // Utility actions
      expandAllCategories: (categoryIds) =>
        set({
          expandedCategories: new Set(categoryIds),
          manuallyToggled: true,
        }),

      collapseAllCategories: () =>
        set({
          expandedCategories: new Set(),
          manuallyToggled: true,
        }),

      // Auto-expand for search (doesn't set manual toggle)
      autoExpandForSearch: (categoryIds) =>
        set({
          expandedCategories: new Set(categoryIds),
        }),

      // Reset all state
      reset: () =>
        set({
          ...initialState,
          categoryDialogOpen: false,
          serviceDialogOpen: false,
          editingCategory: null,
          selectedCategoryForService: null,
          isSearchExpanded: false,
          isEditMode: false,
          serviceDragInProgress: false,
          activeServiceDragId: null,
          showArchived: false,
        }),
    }),
    {
      name: "services-ui-store",
      // Only log actions in development
      ...(process.env.NODE_ENV === "development" && {
        serialize: {
          set: new Set(), // Don't serialize Set objects
        },
      }),
    }
  )
);
